import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useParams } from 'react-router-dom';
import './CaseStudyDetail.css';
import SEO from './components/SEO';
import Breadcrumb from './components/Breadcrumb';
import { caseStudies } from './data/caseStudies';

// 文中の数字（改善率・件数など）を等幅書体にする
const NUMBER_PATTERN = /(\d[\d.,]*(?:[〜~]\d[\d.,]*)?)/;

function withNumbers(text) {
  return text.split(NUMBER_PATTERN).map((part, i) =>
    i % 2 === 1 ? <span key={i} className="ds-num">{part}</span> : part
  );
}

// 読み込み中・見つからない場合の見出し帯
function StatusHero({ id, label, title, lead }) {
  return (
    <div>
      <SEO page="case-study-detail" />
      <section className="ds-page-hero">
        <div className="container">
          <Breadcrumb items={[
            { label: 'ホーム', path: '/' },
            { label: '導入事例', path: '/case-studies' },
            { label, path: `/case-study/${id}` }
          ]} />
          <p className="ds-eyebrow">CASE STUDY</p>
          {title && <h1 className="ds-h1">{title}</h1>}
          <p className="ds-lead">{lead}</p>
        </div>
      </section>
    </div>
  );
}

StatusHero.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string.isRequired,
  title: PropTypes.string,
  lead: PropTypes.string.isRequired
};

function CaseStudyDetail() {
  const { id } = useParams();
  const [caseStudy, setCaseStudy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const found = caseStudies.find(cs => cs.id === id);
    setCaseStudy(found || null);
    setLoading(false);

    // ページ遷移時に最上部にスクロール
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) {
    return <StatusHero id={id} label="読み込み中..." lead="読み込み中..." />;
  }

  if (!caseStudy) {
    return (
      <StatusHero
        id={id}
        label="見つかりません"
        title="導入事例が見つかりません"
        lead="お探しの導入事例は存在しないか、削除された可能性があります。"
      />
    );
  }

  const breadcrumbItems = [
    { label: 'ホーム', path: '/' },
    { label: '導入事例', path: '/case-studies' },
    { label: caseStudy.companyName, path: `/case-study/${caseStudy.id}` }
  ];

  return (
    <div>
      <SEO page="case-study-detail" />

      <section className="ds-page-hero">
        <div className="container">
          <Breadcrumb items={breadcrumbItems} />
          <p className="ds-eyebrow">CASE STUDY</p>
          <h1 className="ds-h1">{caseStudy.companyName}</h1>
          <p className="ds-lead">{withNumbers(caseStudy.summary)}</p>
          {(caseStudy.interviewer || caseStudy.employeeCount) && (
            <ul className="csd-meta">
              {caseStudy.interviewer && <li>{caseStudy.interviewer}</li>}
              {caseStudy.employeeCount && (
                <li>従業員数：<span className="ds-num">{caseStudy.employeeCount}</span></li>
              )}
            </ul>
          )}
        </div>
      </section>

      <section className="ds-section">
        <div className="container">
          <article className="csd-article">
            <figure className="csd-figure">
              <img src={caseStudy.imageUrl} alt={`${caseStudy.companyName}の事例`} />
            </figure>

            {caseStudy.companyDescription && (
              <aside className="ds-card csd-company">
                <p className="ds-eyebrow">COMPANY</p>
                <p className="csd-company-text">{caseStudy.companyDescription}</p>
              </aside>
            )}

            {caseStudy.sections.map((section, index) => (
              <section key={index} className="csd-topic">
                <p className="ds-eyebrow ds-num">{String(index + 1).padStart(2, '0')}</p>
                <h2 className="ds-h3">{section.title}</h2>
                <p className="csd-question">{section.question}</p>
                <p className="csd-answer">{section.answer}</p>
              </section>
            ))}

            <div className="csd-back">
              <Link to="/case-studies" className="ds-link">導入事例一覧へ</Link>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}

export default CaseStudyDetail;
